import React from 'react';
import PolicyPageLayout from './PolicyPageLayout';

const GDPR = () => {
  const sections = [
    {
      title: "Our GDPR Commitment",
      content: [
        "SoundFlowAI is committed to protecting the privacy and data rights of individuals in the European Economic Area (EEA). We comply with the General Data Protection Regulation (GDPR) and have implemented comprehensive measures to ensure your rights are respected.",
        "This page outlines how we process personal data in accordance with GDPR requirements and explains your rights as a data subject."
      ]
    },
    {
      title: "Legal Basis for Processing",
      content: [
        "We process personal data only when we have a valid legal basis to do so. Depending on the type of processing, we rely on one or more of the following legal bases:"
      ],
      bullets: [
        "Consent: When you explicitly agree to specific processing activities",
        "Contract: Processing necessary to provide our services to you",
        "Legitimate Interests: When we have a valid business reason that doesn't override your rights",
        "Legal Obligation: When we're required by law to process your data"
      ]
    },
    {
      title: "Your Data Subject Rights",
      content: [
        "Under GDPR, you have specific rights regarding your personal data. We have implemented systems and processes to help you exercise these rights."
      ],
      bullets: [
        "Right of Access: Request a copy of your personal data",
        "Right to Rectification: Correct inaccurate or incomplete data",
        "Right to Erasure: Request deletion of your data ('right to be forgotten')",
        "Right to Restrict Processing: Limit how we use your data",
        "Right to Data Portability: Receive your data in a machine-readable format",
        "Right to Object: Object to certain types of processing",
        "Right to Withdraw Consent: Withdraw consent at any time"
      ]
    },
    {
      title: "Data Protection Officer",
      content: [
        "We have appointed a Data Protection Officer (DPO) to oversee our GDPR compliance. You can contact our DPO for any questions or concerns about how we handle your personal data.",
        "Email: dpo@soundflowai.com"
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        "When we transfer personal data outside the EEA, we ensure appropriate safeguards are in place to protect your data."
      ],
      bullets: [
        "Standard Contractual Clauses (SCCs) approved by the European Commission",
        "EU-U.S. Data Privacy Framework certification",
        "Binding Corporate Rules for intra-group transfers",
        "Adequacy decisions for transfers to approved countries"
      ]
    },
    {
      title: "Data Retention",
      content: [
        "We retain personal data only for as long as necessary to provide our services and fulfill our legal obligations. Retention periods vary based on the type of data and your relationship with us."
      ],
      bullets: [
        "Account data: Retained while your account is active, plus 30 days",
        "Audio files: 30 days after processing unless stored in your library",
        "Transaction records: 7 years for tax and legal compliance",
        "Support communications: 2 years after resolution",
        "Analytics data: Anonymized after 26 months"
      ]
    },
    {
      title: "Supervisory Authority",
      content: [
        "You have the right to lodge a complaint with a supervisory authority if you believe we have violated your data protection rights. Our lead supervisory authority is the Irish Data Protection Commission.",
        "However, you may also contact the supervisory authority in your country of residence."
      ]
    },
    {
      title: "Data Processing Agreements",
      content: [
        "We enter into Data Processing Agreements (DPAs) with all third-party processors who handle personal data on our behalf. These agreements ensure our processors meet GDPR requirements and protect your data appropriately."
      ]
    }
  ];

  const faqs = [
    {
      question: "How can I exercise my GDPR rights?",
      answer: "You can exercise your rights by logging into your account and navigating to Settings > Privacy, or by contacting our Data Protection Officer at dpo@soundflowai.com. We will respond to requests within 30 days."
    },
    {
      question: "Do you process any special categories of data?",
      answer: "We do not intentionally collect special categories of personal data (such as health data, religious beliefs, etc.). If your audio content contains such data, you are responsible for ensuring you have the appropriate legal basis to process it."
    },
    {
      question: "How do you handle data breach notifications?",
      answer: "In the event of a personal data breach that poses a risk to your rights and freedoms, we will notify you within 72 hours of becoming aware of the breach, as required by GDPR Article 33."
    },
    {
      question: "Can I request a copy of my data?",
      answer: "Yes, you can request a complete copy of your personal data in a machine-readable format (JSON or CSV) through your account settings or by contacting our DPO. This request is free of charge."
    },
    {
      question: "What is your data retention policy?",
      answer: "We retain different types of data for different periods based on legal requirements and legitimate business needs. Generally, account data is deleted 30 days after account closure, and processed audio files are deleted after 30 days unless saved to your library."
    }
  ];

  return (
    <PolicyPageLayout
      title="GDPR Compliance"
      lastUpdated="January 20, 2026"
      sections={sections}
      faqs={faqs}
    />
  );
};

export default GDPR;
